import { useEffect, useRef } from 'react';

/**
 * useRingtone - Plays ringtone for incoming/outgoing calls
 * 
 * @param {boolean} shouldPlay - Whether the ringtone should be playing
 * @param {string} type - 'incoming' | 'outgoing'
 */
const useRingtone = (shouldPlay, type = 'incoming') => {
  const audioRef = useRef(null);

  useEffect(() => {
    if (shouldPlay) {
      // Create audio element if not exists
      if (!audioRef.current) {
        audioRef.current = new Audio();
        audioRef.current.loop = true;
      }

      // Use Web Audio API oscillator for simple ringtone (no external file needed)
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Different tones for incoming vs outgoing
      if (type === 'incoming') {
        // Classic phone ring pattern
        oscillator.frequency.value = 440; // A4
        oscillator.type = 'sine';
        gainNode.gain.value = 0.3;

        // Ring pattern: 1s on, 2s off
        let isRinging = true;
        const ringInterval = setInterval(() => {
          gainNode.gain.value = isRinging ? 0 : 0.3;
          isRinging = !isRinging;
        }, 1000);

        oscillator.start();

        return () => {
          clearInterval(ringInterval);
          oscillator.stop();
          audioContext.close();
        };
      } else {
        // Outgoing: ringback tone pattern
        oscillator.frequency.value = 440;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.15;

        // Ringback pattern: 2s on, 4s off
        let isPlaying = true;
        const ringbackInterval = setInterval(() => {
          gainNode.gain.value = isPlaying ? 0 : 0.15;
          isPlaying = !isPlaying;
        }, 2000);

        oscillator.start();

        return () => {
          clearInterval(ringbackInterval);
          oscillator.stop();
          audioContext.close();
        };
      }
    }
  }, [shouldPlay, type]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);
};

export default useRingtone;
